import { useAnalytics } from '@/contexts/analytics-context/use-analytics';
import { Avatar } from '../../../../../design-system/avatar/avatar/avatar';
import { DrawerClose, DrawerDescription, DrawerHeader, DrawerTitle } from '../../../../../design-system/drawer/drawer';
import { Inline } from '../../../../../design-system/inline/inline';
import { DrawerNavigation } from '../../../../../design-system/navigation/drawer-navigation';
import { Stack } from '../../../../../design-system/stack/stack';
import { TransactionView } from '../../../../../types/entity-types';
import { cn } from '../../../../../utils/cn';
import { navigateToTransaction } from '../utils/navigation';

type HeaderProps = {
  vendor: {
    name: string;
    logo: string;
  };
  amount: string;
  amountWithBaseCurrency: string;
  isCredit: boolean;
  date: string;
  navigationConfig:
    | { visible: false }
    | { visible: true; prev: TransactionView | undefined; next: TransactionView | undefined };
};

export const Header = ({ vendor, amount, isCredit, amountWithBaseCurrency, date, navigationConfig }: HeaderProps) => {
  const analytics = useAnalytics();

  const shouldDisplayBaseCurrencyAmount = amountWithBaseCurrency !== amount;

  const handleNavigatePrev = () => {
    if (navigationConfig.visible && navigationConfig.prev) {
      navigateToTransaction(navigationConfig.prev.id);

      analytics.track('transaction_drawer_navigated', {
        navigation_direction: 'previous',
        transaction_id: navigationConfig.prev.id,
        navigation_source: 'button',
      });
    }
  };

  const handleNavigateNext = () => {
    if (navigationConfig.visible && navigationConfig.next) {
      navigateToTransaction(navigationConfig.next.id);

      analytics.track('transaction_drawer_navigated', {
        navigation_direction: 'next',
        transaction_id: navigationConfig.next.id,
        navigation_source: 'button',
      });
    }
  };

  return (
    <DrawerHeader className="relative flex items-center rounded-lg bg-muted py-8">
      <Stack className="w-full" space={5} justify="center" align="center">
        {navigationConfig.visible && (
          <DrawerNavigation
            navigationConfig={{
              isFirstInvoice: !navigationConfig.prev,
              isLastInvoice: !navigationConfig.next,
              onPrevious: handleNavigatePrev,
              onNext: handleNavigateNext,
            }}
            className="absolute top-3 left-3 bg-white"
          />
        )}

        {vendor.name && (
          <Stack align="center" justify="start" space={2}>
            <Avatar name={vendor.name} avatarUrl={vendor.logo} size="lg" />
            <DrawerTitle>
              <div className="text-center text-base font-medium">{vendor.name}</div>
            </DrawerTitle>
          </Stack>
        )}

        <DrawerDescription>
          <Stack space={1} align="center">
            <div
              className={cn('text-center text-[32px] leading-[40px] font-thin tracking-[-0.02em]', {
                'text-success': isCredit,
                'text-black': !isCredit,
              })}
            >
              {amount}
            </div>
            <Inline space={2}>
              {shouldDisplayBaseCurrencyAmount && (
                <>
                  <div className="text-sm leading-[22px] text-gray-500">{amountWithBaseCurrency}</div>
                  <div className="text-lg leading-[22px] text-gray-500">·</div>
                </>
              )}
              <div className="text-sm leading-[22px] text-gray-500">{date}</div>
            </Inline>
          </Stack>
        </DrawerDescription>

        <DrawerClose />
      </Stack>
    </DrawerHeader>
  );
};
